// React and routing imports
import {useCallback, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'

// Component-specific styles
import './promotionList.scss'

// Custom Organism Components
import {CustomDataTable, FileUploadWithModal} from '@/components/organisms'

// Helpers
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper'

// Custom Atom Components
import {Icon, LinkText, Tag} from '@/components/atoms'

// Custom Hooks
import {usePromotionList} from '@/hooks/usePromotionList'

// Constants
import {TAG_COLOR} from '@/utils/constants/colors'
import {Constants, DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {ROUTES} from '@/utils/constants/routesConstants'

/**
 * PromotionsList component displays a sortable, filterable, and paginated list of promotions.
 */
export const PromotionsList = () => {
  const navigate = useNavigate()
  const [isSelectAll, setSelectAll] = useState(false)
  const [isModal, toggleModal] = useState(false)

  const [rowSelected, setSelectedRows] = useState([])
  const {
    promotionListData,
    promotionListColConfigData,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    onPageIndexChange,
    isLoading,
    createSearchArray,
    resetAllBtnClicked,
    formData,
    setFormData,
    downloadPromoTemplate,
  } = usePromotionList()

  // Handler for the "New Promotion" button click, navigates to the create promotion page.
  const onNewPromotionClick = useCallback(() => {
    navigate(ROUTES.SELECT_PROMOTION_TYPE)
  }, [navigate])

  const handleCopyClick = (promotionId, promotionType) => {
    navigate(ROUTES.CREATE_PROMOTION, {
      state: {
        promotionId,
        isDuplicating: true,
        promotionType,
      },
    })
  }

  // Cell data mapper object for DynamicColumnTable
  const cellDataMapperObj = {
    name: ({row: {original} = {}}) => (
      <LinkText
        text={original.name}
        id={original.id}
        handleClick={() => {
          const promotionId = original.id
          navigate(ROUTES.PROMOTION_DETAIL.replace(':id', promotionId))
        }}
      />
    ),
    promotionType: ({row: {original} = {}}) => original.promotionType,
    noOfStores: ({row: {original} = {}}) => (original.noOfStores === null ? '-' : original.noOfStores),
    percentPromotionAccepted: ({row: {original} = {}}) => {
      const percentage = original.percentagePromotionAccepted
      const showTag = typeof percentage === 'number' && percentage < 90

      if (percentage === null) return '-'
      if (showTag) {
        return <Tag text={`${percentage}%`} color={TAG_COLOR.RED_600} />
      }
      return `${percentage}%`
    },
    displayOrder: ({row: {original} = {}}) => original.displayOrder,
    activeStartDate: ({row: {original} = {}}) => {
      const dateString = original.activeStartDate
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    activeEndDate: ({row: {original} = {}}) => {
      const dateString = original.activeEndDate
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    status: ({row: {original} = {}}) => {
      let {status, id, promotionType} = original
      status = status?.[0] + status?.slice(1).toLocaleLowerCase()
      return (
        <div className="flex flex-row items-center justify-end gap-2">
          <p>{status}</p>
          <button className="cursor-pointer" onClick={() => handleCopyClick(id, promotionType)} title="Duplicate">
            <Icon
              icon="content_copy"
              size="medium"
              className="!ml-[40px] flex h-10 w-10 items-center justify-center rounded-sm border border-[#4e4e4e] !text-[#0268d7]"
            />
          </button>
        </div>
      )
    },
  }

  // Get cell data mapped columns
  const cellDataMappedCols = getCellDataMappedCols(cellDataMapperObj, promotionListColConfigData)

  const transformedColumns = useMemo(
    () =>
      cellDataMappedCols.map(col => {
        const baseConfig = {
          name_of_db_column: col.name_of_db_column,
          label_of_column: col.columnFilterPlaceholder,
          filterVariant: col.filterVariant || 'text',
        }

        // Conditional enhancements
        if (col.name_of_db_column === 'name') {
          return {
            ...baseConfig,
            isClickable: true,
            onCellClick: (value, row) => {
              if (value && row?.id) {
                navigate(ROUTES.PROMOTION_DETAIL.replace(':id', row.id))
              }
            },
          }
        }

        if (col.name_of_db_column === 'status') {
          return {
            ...baseConfig,
            isClickable: true,
            isIconShow: true,
            iconTooltip: 'Duplicate',
          }
        }

        return baseConfig
      }),
    [cellDataMappedCols, navigate]
  )

  const handleResetAll = () => {
    setSortingObj({})
    createSearchArray([])
    setSearchArry({})
    setSelectedRows([])
  }

  const tableActions = useMemo(
    () => [
      // Primary action – create a new promotion
      {
        id: 'new-promotion',
        label: 'New Promotion',
        icon: 'add_box',
        onClick: () => onNewPromotionClick(),
        disabled: false,
      },
      // Divider between "New" and other actions – use a simple object flag
      {divider: true},
      // Existing download action

      {
        id: 'upload',
        label: 'Upload Bulk Promotions',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          toggleModal(!isModal)
        },
      },
      {
        id: 'upload_images',
        label: 'Upload Images',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          const dataToDownload = isSelectAll ? [] : rowSelected
          const productIds = dataToDownload.map(promo => promo.id)

          onDownloadBtnClick(productIds)
        },
      },
      {
        id: 'upload_history',
        label: 'View Upload History',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          const dataToDownload = isSelectAll ? [] : rowSelected
          const productIds = dataToDownload.map(promo => promo.id)

          onDownloadBtnClick(productIds)
        },
      },
      {
        id: 'download',
        label: 'Download',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          const dataToDownload = isSelectAll ? [] : rowSelected
          const productIds = dataToDownload.map(promo => promo.id)

          onDownloadBtnClick(productIds)
        },
        disabled: ({selectedRows}) => !selectedRows?.length, // Dynamic disable based on selection
      },
    ],
    [isSelectAll, onDownloadBtnClick, onNewPromotionClick, rowSelected]
  )

  const handleSorting = ({column, direction}) => {
    setSortingObj({column, direction})
  }

  // Handle page index change for CustomDataTable
  const handlePageIndexChange = pageIndex => {
    onPageIndexChange(pageIndex)
  }
  return (
    <div className="product-admin-promotions-list">
      <CustomDataTable
        columns={transformedColumns}
        records={promotionListData?.data || []}
        setSearchArry={setSearchArry}
        handleSorting={handleSorting}
        setPageIndex={handlePageIndexChange}
        pageSize={DEFAULT_TABLE_PAGE_SIZE}
        totalRecords={promotionListData?.totalRecords}
        onResetAll={() => {
          handleResetAll()
          resetAllBtnClicked()
        }}
        onRowSelect={selectedRows => setSelectedRows(selectedRows)}
        tableHeader="Promotions List"
        isLoading={isLoading}
        actions={tableActions}
        id="id"
        showActions={true}
        copyIconClick={row => handleCopyClick(row?.id, row?.promotionType)}
        isSelectAll={isSelectAll}
        setSelectAll={setSelectAll}
        defaultSortConfig={{key: 'activeStartDate', direction: 'desc'}}
      />
      <FileUploadWithModal
        openUpload={isModal}
        setOpenUpload={toggleModal}
        setFormData={setFormData}
        formData={formData}
        title="Bulk Upload Promotions"
        showTertiaryButton={true}
        uploadFor={'product'}
        isCsvValidationRequired={false}
        isTemplateUpload={true}
        label={'Upload Bulk Promotion file'}
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={downloadPromoTemplate}
        showTertiaryDownloadButton={true}
        acceptedExtensions={Constants.Xlsx}
      />
    </div>
  )
}
